import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, User, FileText, Download, CheckCircle, XCircle, Clock, Copy, Loader2, Users } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import AdminLayout from '@/components/AdminLayout';
import { getAdminRequest, approveRequest, rejectRequest, getCustomers } from '@/lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminRequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [generatedCode, setGeneratedCode] = useState('');
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [downloadingDoc, setDownloadingDoc] = useState(null);
  
  // Customer pricing selection
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);

  const handleDownloadDocument = async (docType, originalName) => {
    setDownloadingDoc(docType);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/documents/${id}/${docType}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = originalName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Document downloaded');
    } catch (error) {
      toast.error('Failed to download document');
    } finally {
      setDownloadingDoc(null);
    }
  };

  useEffect(() => {
    fetchRequest();
    fetchCustomers();
  }, [id]);

  const fetchRequest = async () => {
    setIsLoading(true);
    try {
      const response = await getAdminRequest(id);
      setRequest(response.data);
    } catch (error) {
      toast.error('Failed to load request details');
      navigate('/admin/requests');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCustomers = async () => {
    setIsLoadingCustomers(true);
    try {
      const response = await getCustomers();
      setCustomers(response.data.customers || []);
    } catch (error) {
      console.error('Failed to load customers:', error);
    } finally {
      setIsLoadingCustomers(false);
    }
  };

  const handleApprove = async () => {
    setIsApproving(true);
    try {
      const response = await approveRequest(id, selectedCustomerId || null);
      setGeneratedCode(response.data.access_code);
      setShowApprovalDialog(true);
      setRequest({ ...request, status: 'approved' });
      toast.success('Request approved successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve request');
    } finally {
      setIsApproving(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    setIsRejecting(true);
    try {
      await rejectRequest(id, rejectReason);
      setShowRejectDialog(false);
      setRequest({ ...request, status: 'rejected', rejection_reason: rejectReason });
      toast.success('Request rejected');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject request');
    } finally {
      setIsRejecting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return (
          <Badge variant="outline" className="border-amber-500/50 text-amber-400 bg-amber-500/10">
            <Clock className="w-3 h-3 mr-1" />
            Pending Review
          </Badge>
        );
      case 'approved':
        return (
          <Badge variant="outline" className="border-emerald-500/50 text-emerald-400 bg-emerald-500/10">
            <CheckCircle className="w-3 h-3 mr-1" />
            Approved
          </Badge>
        );
      case 'rejected':
        return (
          <Badge variant="outline" className="border-red-500/50 text-red-400 bg-red-500/10">
            <XCircle className="w-3 h-3 mr-1" />
            Rejected
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-24">
          <span className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  if (!request) {
    return (
      <AdminLayout>
        <div className="text-center py-24">
          <p className="text-muted-foreground">Request not found</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-4xl">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/admin/requests')}
          className="gap-2"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Requests
        </Button>

        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-primary/10">
              {request.applicant_type === 'company' ? (
                <Building2 className="w-6 h-6 text-primary" />
              ) : (
                <User className="w-6 h-6 text-primary" />
              )}
            </div>
            <div>
              <h1 className="font-serif text-2xl font-semibold text-foreground">
                {request.full_name}
              </h1>
              <p className="text-muted-foreground">{request.email}</p>
            </div>
          </div>
          {getStatusBadge(request.status)}
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Request Details */}
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="text-base font-medium">Request Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground text-xs">Applicant Type</Label>
                  <p className="font-medium capitalize">{request.applicant_type}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">Submitted</Label>
                  <p className="font-medium">{formatDate(request.created_at)}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground text-xs">Full Name</Label>
                  <p className="font-medium">{request.full_name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">Email</Label>
                  <p className="font-medium">{request.email}</p>
                </div>
              </div>
              {request.company_name && (
                <div>
                  <Label className="text-muted-foreground text-xs">Company Name</Label>
                  <p className="font-medium">{request.company_name}</p>
                </div>
              )}
              {request.country && (
                <div>
                  <Label className="text-muted-foreground text-xs">Country</Label>
                  <p className="font-medium">{request.country}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* NDA Info */}
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="text-base font-medium">NDA Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground text-xs">NDA Version</Label>
                  <p className="font-medium font-mono">{request.nda_version}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">Accepted At</Label>
                  <p className="font-medium">{formatDate(request.nda_accepted_at)}</p>
                </div>
              </div>
              {request.rejection_reason && (
                <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/20">
                  <Label className="text-destructive text-xs">Rejection Reason</Label>
                  <p className="text-sm text-foreground mt-1">{request.rejection_reason}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Documents */}
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="text-base font-medium">Uploaded Documents</CardTitle>
            <CardDescription>
              {request.documents?.length || 0} document(s) attached
            </CardDescription>
          </CardHeader>
          <CardContent>
            {request.documents && request.documents.length > 0 ? (
              <div className="grid gap-4">
                {request.documents.map((doc, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-4 rounded-lg bg-background/50 border border-border"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <FileText className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{doc.original_name}</p>
                        <p className="text-xs text-muted-foreground capitalize">
                          {doc.type.replace('_', ' ')} • {(doc.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      onClick={() => handleDownloadDocument(doc.type, doc.original_name)}
                      disabled={downloadingDoc === doc.type}
                      data-testid={`download-${doc.type}`}
                    >
                      {downloadingDoc === doc.type ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4" />
                      )}
                      Download
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">No documents uploaded</p>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        {request.status === 'pending' && (
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <Users className="w-4 h-4" />
                Approval Settings
              </CardTitle>
              <CardDescription>
                Optionally assign a customer pricing profile before approving
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Customer Pricing Selection */}
              <div className="space-y-2">
                <Label>Customer Pricing Profile (Optional)</Label>
                <Select 
                  value={selectedCustomerId || "none"} 
                  onValueChange={(value) => setSelectedCustomerId(value === "none" ? "" : value)}
                  disabled={isLoadingCustomers}
                >
                  <SelectTrigger className="bg-background/50" data-testid="customer-profile-select">
                    <SelectValue placeholder="Select a pricing profile (or leave empty for master pricing)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      <span className="text-muted-foreground">No profile - Use Master Pricing</span>
                    </SelectItem>
                    {customers.map(customer => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.name}
                        {customer.email && <span className="text-muted-foreground ml-2">({customer.email})</span>}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {selectedCustomerId 
                    ? `This access code will use "${customers.find(c => c.id === selectedCustomerId)?.name}" pricing adjustments.`
                    : 'No customer profile selected. The access code will use master pricing.'
                  }
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4">
                <Button
                  onClick={handleApprove}
                  disabled={isApproving}
                  className="flex-1 gold-glow-hover"
                  data-testid="approve-btn"
                >
                  {isApproving ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                      Approving...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Approve & Generate Code
                    </span>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowRejectDialog(true)}
                  className="flex-1 border-destructive/50 text-destructive hover:bg-destructive/10"
                  data-testid="reject-btn"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Reject Dialog */}
        <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reject Request</DialogTitle>
              <DialogDescription>
                Please provide a reason for rejecting this NDA request.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter rejection reason..."
                rows={4}
                data-testid="reject-reason-input"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleReject}
                disabled={isRejecting}
                data-testid="confirm-reject-btn"
              >
                {isRejecting ? 'Rejecting...' : 'Confirm Rejection'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Approval Success Dialog */}
        <Dialog open={showApprovalDialog} onOpenChange={setShowApprovalDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-500" />
                Request Approved
              </DialogTitle>
              <DialogDescription>
                The access code has been generated and sent to {request.email}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label className="text-muted-foreground text-xs">Generated Access Code</Label>
              <div className="flex items-center gap-2 mt-2">
                <Input
                  value={generatedCode}
                  readOnly
                  className="font-mono text-lg tracking-wider text-center bg-background/50"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => copyToClipboard(generatedCode)}
                  data-testid="copy-code-btn"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={() => setShowApprovalDialog(false)}>Done</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminRequestDetail;
